/**
 * ExpandableKPI — Click-to-expand KPI card with progressive disclosure
 */
import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors } from '../../styles/colors';

function ExpandableKPI({
  value,
  label,
  valueLabel,
  delta,
  deltaType = 'neutral', // 'positive' | 'negative' | 'neutral'
  trend,  // 'up' | 'down' | 'flat'
  expandedContent,
  icon,
  badge,
  dark = false,
  delay = 0,
  info,
  summary,
  isExpanded: controlledExpanded,
  onToggle,
}) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const deltaColor = deltaType === 'positive' ? colors.success
    : deltaType === 'negative' ? colors.danger
    : colors.textMed;

  const glowGradient = isHovered
    ? `radial-gradient(250px circle at ${mousePos.x}px ${mousePos.y}px, rgba(27,94,64,0.07), transparent 60%)`
    : 'none';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ flex: '1 1 0' }}
    >
      <Box
        ref={cardRef}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => { setIsHovered(true); setShowInfo(true); }}
        onMouseLeave={() => { setIsHovered(false); setShowInfo(false); }}
        onClick={() => {
          if (!expandedContent) return;
          if (onToggle) onToggle();
          else setInternalExpanded(!internalExpanded);
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && expandedContent) {
            e.preventDefault();
            if (onToggle) onToggle();
            else setInternalExpanded(!internalExpanded);
          }
        }}
        sx={{
          position: 'relative',
          borderRadius: '20px',
          border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : colors.borderLight}`,
          background: dark ? 'rgba(255,255,255,0.06)' : colors.cardGlass,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          p: '24px 28px',
          cursor: expandedContent ? 'pointer' : 'default',
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
            transform: 'translateY(-2px)',
            borderColor: dark ? 'rgba(255,255,255,0.12)' : colors.primaryLight,
          },
          '&:hover .kpi-value': {
            color: dark ? colors.primaryBright : colors.primary,
          },
          '&:focus-visible': {
            outline: `2px solid ${colors.primary}`,
            outlineOffset: '2px',
          },
        }}
      >
        {/* Mouse glow */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: glowGradient,
            pointerEvents: 'none',
            zIndex: 0,
            borderRadius: 'inherit',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header row: icon + badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {icon && (
                <Box sx={{ color: dark ? colors.primaryBright : colors.primary, display: 'flex' }}>
                  {icon}
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: dark ? 'rgba(250,247,242,0.6)' : colors.textMed,
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  letterSpacing: '0.02em',
                  textTransform: 'none',
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                {label}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {badge && (
                <Chip
                  label={badge}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: dark ? 'rgba(27,94,64,0.3)' : 'rgba(27,94,64,0.08)',
                    color: dark ? colors.primaryBright : colors.primary,
                  }}
                />
              )}
              {expandedContent && (
                <motion.div
                  animate={{ rotate: expanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ExpandMoreIcon
                    sx={{ fontSize: 18, color: dark ? 'rgba(250,247,242,0.4)' : colors.textLight }}
                  />
                </motion.div>
              )}
            </Box>
          </Box>

          {/* Value */}
          <Typography
            className="kpi-value"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: '2.6rem',
              fontWeight: 500,
              color: dark ? colors.ivory : colors.charcoal,
              lineHeight: 1.1,
              mb: 0.75,
              fontVariantNumeric: 'tabular-nums',
              transition: 'color 0.2s ease',
            }}
          >
            {value}
          </Typography>

          {/* Value label (contextual subtitle) */}
          {valueLabel && (
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.72rem', color: dark ? 'rgba(250,247,242,0.4)' : colors.textLight,
              fontWeight: 400, mt: -0.25,
            }}>
              {valueLabel}
            </Typography>
          )}

          {/* Summary text */}
          {summary && (
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.78rem',
                color: dark ? 'rgba(250,247,242,0.5)' : colors.textMed,
                lineHeight: 1.4,
                mt: 0.5,
              }}
            >
              {summary}
            </Typography>
          )}

          {/* Delta — simple text */}
          {(delta !== undefined && delta !== null) && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: deltaColor,
                mt: '10px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {delta}
            </Typography>
          )}

          {/* Expanded content */}
          <AnimatePresence>
            {expanded && expandedContent && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                style={{ overflow: 'hidden' }}
              >
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : colors.borderLight}`,
                  }}
                >
                  {expandedContent}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Glassmorphism info tooltip on hover — emerald tint + slide-up */}
        <AnimatePresence>
          {showInfo && info && !expanded && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.3, exit: { duration: 0.2 } }}
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: dark
                  ? 'linear-gradient(135deg, rgba(27,94,64,0.12) 0%, rgba(26,26,24,0.92) 100%)'
                  : 'linear-gradient(135deg, rgba(27,94,64,0.04) 0%, rgba(250,247,242,0.95) 100%)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '0 0 20px 20px',
                padding: '14px 22px',
                borderTop: '1px solid rgba(27,94,64,0.15)',
                zIndex: 5,
              }}
            >
              <Typography sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.82rem',
                color: dark ? 'rgba(250,247,242,0.7)' : colors.textMed,
                lineHeight: 1.6, textAlign: 'justify',
              }}>
                {info}
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </motion.div>
  );
}

export default ExpandableKPI;
